import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAcademicService } from "../apis/academic/useAcademicService";
import {
  TCourse,
  TCreateCourseRequest,
  TUpdateCourseRequest,
  TClass,
  TCreateClassRequest,
  TUpdateClassRequest,
  TGradeLevel,
  TCreateGradeLevelRequest,
  TUpdateGradeLevelRequest,
  TSyllabus,
  TCreateSyllabusRequest,
  TUpdateSyllabusRequest,
  TSubject,
  TCreateSubjectRequest,
  TUpdateSubjectRequest,
  TSection,
  TClassSubject,
  TSectionTeacher,
  TCreateSectionRequest,
  TUpdateSectionRequest,
  TAssignClassSubjectRequest,
  TUpdateClassSubjectRequest,
} from "../apis/academic/academic.interfaces";
import { TInternalUser } from "../apis/users/users.interfaces";
import { useInstituteContext } from "../context/institute.context";
import { mergePaginationQuery } from "../apis/common.interfaces";

export const useCourseHook = () => {
  const { getCourses } = useAcademicService();
  const { selectedInstitute } = useInstituteContext();

  return useQuery<TCourse[]>({
    queryKey: ["courses", selectedInstitute?.id],
    queryFn: async () => {
      const res = await getCourses(
        mergePaginationQuery({ page: 1, limit: 500 }),
      );
      return res.data;
    },
    enabled: !!selectedInstitute?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCourseByIdHook = (id?: string) => {
  const { getCourseById } = useAcademicService();

  return useQuery<TCourse>({
    queryKey: ["course", id],
    queryFn: () => getCourseById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateCourseHook = () => {
  const { createCourse } = useAcademicService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<TCourse, Error, TCreateCourseRequest>({
    mutationFn: (request: TCreateCourseRequest) => createCourse(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["courses", selectedInstitute?.id],
      });
    },
  });
};

export const useUpdateCourseHook = () => {
  const { updateCourse } = useAcademicService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TCourse,
    Error,
    { id: string; request: TUpdateCourseRequest }
  >({
    mutationFn: ({ id, request }) => updateCourse(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["courses", selectedInstitute?.id],
      });
    },
  });
};

export const useDeleteCourseHook = () => {
  const { deleteCourse } = useAcademicService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => deleteCourse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["courses", selectedInstitute?.id],
      });
    },
  });
};

export const useClassHook = () => {
  const { getClasses } = useAcademicService();
  const { selectedInstitute } = useInstituteContext();

  return useQuery<TClass[]>({
    queryKey: ["classes", selectedInstitute?.id],
    queryFn: async () => {
      const res = await getClasses(
        mergePaginationQuery({ page: 1, limit: 500 }),
      );
      return res.data;
    },
    enabled: !!selectedInstitute?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useClassByIdHook = (id?: string) => {
  const { getClassById } = useAcademicService();

  return useQuery<TClass>({
    queryKey: ["class", id],
    queryFn: () => getClassById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateClassHook = () => {
  const { createClass } = useAcademicService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TClass,
    Error,
    { courseId: string; request: TCreateClassRequest }
  >({
    mutationFn: ({ courseId, request }) => createClass(courseId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["classes", selectedInstitute?.id],
      });
    },
  });
};

export const useUpdateClassHook = () => {
  const { updateClass } = useAcademicService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TClass,
    Error,
    { id: string; request: TUpdateClassRequest }
  >({
    mutationFn: ({ id, request }) => updateClass(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["classes", selectedInstitute?.id],
      });
    },
  });
};

export const useDeleteClassHook = () => {
  const { deleteClass } = useAcademicService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => deleteClass(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["classes", selectedInstitute?.id],
      });
    },
  });
};

export const useGradeLevelHook = () => {
  const { getGradeLevels } = useAcademicService();
  const { selectedInstitute } = useInstituteContext();

  return useQuery<TGradeLevel[]>({
    queryKey: ["scoring-rubrics", selectedInstitute?.id],
    queryFn: () => getGradeLevels(selectedInstitute?.id),
    enabled: !!selectedInstitute?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useGradeLevelByIdHook = (id?: string) => {
  const { getGradeLevelById } = useAcademicService();

  return useQuery<TGradeLevel>({
    queryKey: ["grade-level", id],
    queryFn: () => getGradeLevelById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateGradeLevelHook = () => {
  const { createGradeLevel } = useAcademicService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<TGradeLevel, Error, TCreateGradeLevelRequest>({
    mutationFn: (request: TCreateGradeLevelRequest) =>
      createGradeLevel(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["scoring-rubrics", selectedInstitute?.id],
      });
    },
  });
};

export const useUpdateGradeLevelHook = () => {
  const { updateGradeLevel } = useAcademicService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TGradeLevel,
    Error,
    { id: string; request: TUpdateGradeLevelRequest }
  >({
    mutationFn: ({ id, request }) => updateGradeLevel(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["scoring-rubrics", selectedInstitute?.id],
      });
    },
  });
};

export const useDeleteGradeLevelHook = () => {
  const { deleteGradeLevel } = useAcademicService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => deleteGradeLevel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["scoring-rubrics", selectedInstitute?.id],
      });
    },
  });
};

export const useSubjectsHook = () => {
  const { getSubjects } = useAcademicService();
  const { selectedInstitute } = useInstituteContext();

  return useQuery<TSubject[]>({
    queryKey: ["subjects", selectedInstitute?.id],
    queryFn: async () => {
      const res = await getSubjects(
        mergePaginationQuery({ page: 1, limit: 500 }),
      );
      return res.data;
    },
    enabled: !!selectedInstitute?.id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useSubjectByIdHook = (id?: string) => {
  const { getSubjectById } = useAcademicService();

  return useQuery<TSubject>({
    queryKey: ["subject", id],
    queryFn: () => getSubjectById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useSectionsByClassHook = (classId?: string) => {
  const { listSectionsByClass } = useAcademicService();

  return useQuery<TSection[]>({
    queryKey: ["sections-by-class", classId],
    queryFn: () => listSectionsByClass(classId!),
    enabled: !!classId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useSectionByIdHook = (id?: string) => {
  const { getSectionById } = useAcademicService();

  return useQuery<TSection>({
    queryKey: ["section", id],
    queryFn: () => getSectionById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useClassSubjectsHook = (classId?: string) => {
  const { listClassSubjects } = useAcademicService();

  return useQuery<TClassSubject[]>({
    queryKey: ["class-subjects", classId],
    queryFn: () => listClassSubjects(classId!),
    enabled: !!classId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useSectionTeachersHook = (sectionId?: string) => {
  const { listSectionTeachers } = useAcademicService();

  return useQuery<TSectionTeacher[]>({
    queryKey: ["section-teachers", sectionId],
    queryFn: () => listSectionTeachers(sectionId!),
    enabled: !!sectionId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateSubjectHook = () => {
  const { createSubject } = useAcademicService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<TSubject, Error, TCreateSubjectRequest>({
    mutationFn: createSubject,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subjects", selectedInstitute?.id],
      });
    },
  });
};

export const useCreateSectionHook = () => {
  const { createSection } = useAcademicService();
  const queryClient = useQueryClient();

  return useMutation<
    TSection,
    Error,
    { classId: string; request: TCreateSectionRequest }
  >({
    mutationFn: ({ classId, request }) => createSection(classId, request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["sections-by-class", variables.classId],
      });
    },
  });
};

export const useAssignClassSubjectHook = () => {
  const { assignClassSubject } = useAcademicService();
  const queryClient = useQueryClient();

  return useMutation<
    TClassSubject,
    Error,
    { classId: string; request: TAssignClassSubjectRequest }
  >({
    mutationFn: ({ classId, request }) =>
      assignClassSubject(classId, request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["class-subjects", variables.classId],
      });
    },
  });
};

export const useUpdateSubjectHook = () => {
  const { updateSubject } = useAcademicService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TSubject,
    Error,
    { id: string; request: TUpdateSubjectRequest }
  >({
    mutationFn: ({ id, request }) => updateSubject(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subjects", selectedInstitute?.id],
      });
    },
  });
};

export const useDeleteSubjectHook = () => {
  const { deleteSubject } = useAcademicService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => deleteSubject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subjects", selectedInstitute?.id],
      });
    },
  });
};

export const useUpdateSectionHook = () => {
  const { updateSection } = useAcademicService();
  const queryClient = useQueryClient();

  return useMutation<
    TSection,
    Error,
    { id: string; classId: string; request: TUpdateSectionRequest }
  >({
    mutationFn: ({ id, request }) => updateSection(id, request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["sections-by-class", variables.classId],
      });
    },
  });
};

export const useDeleteSectionHook = () => {
  const { deleteSection } = useAcademicService();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { id: string; classId: string }>({
    mutationFn: ({ id }) => deleteSection(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["sections-by-class", variables.classId],
      });
    },
  });
};

export const useUpdateClassSubjectHook = () => {
  const { updateClassSubject } = useAcademicService();
  const queryClient = useQueryClient();

  return useMutation<
    TClassSubject,
    Error,
    {
      classId: string;
      subjectId: string;
      request: TUpdateClassSubjectRequest;
    }
  >({
    mutationFn: ({ classId, subjectId, request }) =>
      updateClassSubject(classId, subjectId, request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["class-subjects", variables.classId],
      });
    },
  });
};

export const useUnassignClassSubjectHook = () => {
  const { unassignClassSubject } = useAcademicService();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { classId: string; subjectId: string }>({
    mutationFn: ({ classId, subjectId }) =>
      unassignClassSubject(classId, subjectId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["class-subjects", variables.classId],
      });
    },
  });
};

export const useAssignSectionTeacherHook = () => {
  const { assignSectionTeacher } = useAcademicService();
  const queryClient = useQueryClient();

  return useMutation<
    TSectionTeacher,
    Error,
    { sectionId: string; userId: string; roleLabel?: string }
  >({
    mutationFn: ({ sectionId, userId, roleLabel }) =>
      assignSectionTeacher(sectionId, userId, roleLabel),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["section-teachers", variables.sectionId],
      });
    },
  });
};

export const useUnassignSectionTeacherHook = () => {
  const { unassignSectionTeacher } = useAcademicService();
  const queryClient = useQueryClient();

  return useMutation<void, Error, { sectionId: string; userId: string }>({
    mutationFn: ({ sectionId, userId }) =>
      unassignSectionTeacher(sectionId, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["section-teachers", variables.sectionId],
      });
    },
  });
};

export const useSyllabusHook = () => {
  const { getSyllabi } = useAcademicService();
  const { selectedInstitute } = useInstituteContext();

  return useQuery<TSyllabus[]>({
    queryKey: ["syllabi", selectedInstitute?.id],
    queryFn: () => getSyllabi(selectedInstitute?.id),
    enabled: !!selectedInstitute?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useSyllabusByIdHook = (id?: string) => {
  const { getSyllabusById } = useAcademicService();

  return useQuery<TSyllabus>({
    queryKey: ["syllabus", id],
    queryFn: () => getSyllabusById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateSyllabusHook = () => {
  const { createSyllabus } = useAcademicService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<TSyllabus, Error, TCreateSyllabusRequest>({
    mutationFn: (request: TCreateSyllabusRequest) => createSyllabus(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["syllabi", selectedInstitute?.id],
      });
    },
  });
};

export const useUpdateSyllabusHook = () => {
  const { updateSyllabus } = useAcademicService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TSyllabus,
    Error,
    { id: string; request: TUpdateSyllabusRequest }
  >({
    mutationFn: ({ id, request }) => updateSyllabus(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["syllabi", selectedInstitute?.id],
      });
    },
  });
};

export const useDeleteSyllabusHook = () => {
  const { deleteSyllabus } = useAcademicService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => deleteSyllabus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["syllabi", selectedInstitute?.id],
      });
    },
  });
};

export const useSyllabiByCourseHook = (courseId?: string) => {
  const { getSyllabiByCourse } = useAcademicService();
  const { selectedInstitute } = useInstituteContext();

  return useQuery<TSyllabus[]>({
    queryKey: ["syllabi-by-course", courseId, selectedInstitute?.id],
    queryFn: () => getSyllabiByCourse(courseId!, selectedInstitute?.id),
    enabled: !!courseId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useSyllabiByClassHook = (classId?: string) => {
  const { getSyllabiByClass } = useAcademicService();
  const { selectedInstitute } = useInstituteContext();

  return useQuery<TSyllabus[]>({
    queryKey: ["syllabi-by-class", classId, selectedInstitute?.id],
    queryFn: () => getSyllabiByClass(classId!, selectedInstitute?.id),
    enabled: !!classId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useAssignSyllabusHook = () => {
  const { assignSyllabus } = useAcademicService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TSyllabus,
    Error,
    {
      syllabusId: string;
      courseId?: string;
      classId?: string;
      academicYear?: string;
    }
  >({
    mutationFn: ({ syllabusId, courseId, classId, academicYear }) =>
      assignSyllabus(syllabusId, courseId, classId, academicYear),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["syllabi", selectedInstitute?.id],
      });
      queryClient.invalidateQueries({ queryKey: ["syllabi-by-course"] });
      queryClient.invalidateQueries({ queryKey: ["syllabi-by-class"] });
    },
  });
};

export const useUnassignSyllabusHook = () => {
  const { unassignSyllabus } = useAcademicService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<TSyllabus, Error, string>({
    mutationFn: (syllabusId: string) => unassignSyllabus(syllabusId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["syllabi", selectedInstitute?.id],
      });
      queryClient.invalidateQueries({ queryKey: ["syllabi-by-course"] });
      queryClient.invalidateQueries({ queryKey: ["syllabi-by-class"] });
    },
  });
};

export const useInternalUsersHook = (search?: string) => {
  const { getInternalUsers } = useAcademicService();
  const { selectedInstitute } = useInstituteContext();

  return useQuery<TInternalUser[]>({
    queryKey: ["internal-users", selectedInstitute?.id, search],
    queryFn: () => getInternalUsers(search),
    enabled: !!selectedInstitute?.id,
    staleTime: 2 * 60 * 1000,
  });
};
